import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button } from '@bb/ui';
import { ApiError, type DataClass } from '../api/client.ts';
import { usePermissions } from '../features/access/usePermissions.ts';

const LABEL: Record<DataClass, { title: string; hint: string }> = {
  raw_events: { title: 'Raw events and sessions', hint: 'custom events and app sessions from SDKs and the server API' },
  interaction_facts: { title: 'Message interaction facts', hint: 'sends, opens, clicks, impressions; also the retargeting history' },
  message_payloads: { title: 'Message payloads', hint: 'rendered content of sent messages' },
  audit: { title: 'Audit log', hint: 'who changed what; at least 365 days' },
  sdk_debug_logs: { title: 'SDK debug logs', hint: 'event log of test users' },
};
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const shortTime = (iso: string) => `${iso.slice(0, 16).replace('T', ' ')} UTC`;

/** Settings → Data retention (BB-22): how long each class of data is kept; aggregated reports are never removed. */
export function RetentionPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/retention' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/retention' });
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canEdit = has('env.manage');
  const queryKey = ['retention', environmentId];
  const policies = useQuery({ queryKey, queryFn: () => api.listRetention(environmentId) });
  const [draft, setDraft] = useState<Partial<Record<DataClass, string>>>({});
  const save = useMutation({
    mutationFn: () =>
      api.updateRetention(
        environmentId,
        Object.entries(draft).map(([dataClass, days]) => ({ dataClass: dataClass as DataClass, retainDays: Number(days) })),
      ),
    onSuccess: (items) => {
      queryClient.setQueryData(queryKey, items);
      setDraft({});
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (Object.keys(draft).length) save.mutate();
  };

  return (
    <section>
      <h1>Data retention</h1>
      <p className="hint">
        Data older than the period is deleted by a daily job. A new or changed period first runs once as a dry run that only counts what it would delete. Aggregated reports are kept.
      </p>
      {policies.isPending ? (
        <p aria-busy="true">Loading policies…</p>
      ) : policies.isError ? (
        <p role="alert">Policies could not be loaded.</p>
      ) : (
        <form aria-label="Retention policies" onSubmit={submit}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Data</th>
                <th scope="col">Keep (days)</th>
                <th scope="col">Last run</th>
              </tr>
            </thead>
            <tbody>
              {policies.data.map((p) => (
                <tr key={p.dataClass}>
                  <td>
                    {LABEL[p.dataClass].title}
                    <div className="hint">{LABEL[p.dataClass].hint}</div>
                  </td>
                  <td>
                    {canEdit ? (
                      <input
                        type="number"
                        min={1}
                        max={3650}
                        aria-label={`${LABEL[p.dataClass].title}, days`}
                        value={draft[p.dataClass] ?? String(p.retainDays)}
                        onChange={(e) => setDraft({ ...draft, [p.dataClass]: e.target.value })}
                      />
                    ) : (
                      p.retainDays
                    )}
                  </td>
                  <td>
                    {p.dryRunPending ? (
                      <span className="tag tag--warning">Dry run pending</span>
                    ) : p.lastRun ? (
                      `${p.lastRun.status === 'failed' ? 'Failed' : p.lastRun.status === 'dry_run' ? 'Dry run' : 'Deleted'} · ${p.lastRun.rowsAffected} rows · ${shortTime(p.lastRun.startedAt)}`
                    ) : (
                      <span className="hint">Not run yet</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {canEdit && (
            <Button type="submit" disabled={save.isPending || Object.keys(draft).length === 0}>
              Save periods
            </Button>
          )}
          {save.isError && <span role="alert">{errorText(save.error)}</span>}
        </form>
      )}
    </section>
  );
}
